"use client";
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useLayoutEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { usePixelatedConfig } from "../config/config.client";
import { SmartImage } from "../elements/smartimage";
import { SmartErrorBoundary } from "../foundation/smarterrorboundary";
import "../../css/pixelated.grid.scss";
import "./page-blocks.scss";
/* ========== LAYOUT COMPONENTS ==========
Reusable, scalable layout components for grid and flex layouts.
These components can be used in the pagebuilder to create
responsive, customizable page sections. */
// Define option arrays - used by both PropTypes and form generation
export const layoutTypes = ['grid', 'flex', 'none'];
export const autoFlowValues = ['row', 'column', 'dense', 'row dense', 'column dense'];
export const justifyItemsValues = ['start', 'center', 'end', 'stretch'];
export const flexDirections = ['row', 'column', 'row-reverse', 'column-reverse'];
export const flexWraps = ['nowrap', 'wrap', 'wrap-reverse'];
export const justifyContentValues = ['start', 'center', 'end', 'space-between', 'space-around', 'space-evenly'];
export const alignItemsValues = ['start', 'center', 'end', 'stretch', 'baseline'];
// ========== PAGE BACKGROUND ==========
/**
 * Page background component.
 * @param param0 - Props containing the image URL.
 * @returns JSX.Element representing the page background.
 */
PageBg.propTypes = {
    image: PropTypes.string.isRequired,
};
export function PageBg(props) {
    const { image } = props;
    return (_jsx("div", { className: "page-bg", children: _jsx(SmartImage, { src: image, alt: "", width: 1920, height: 1080, className: "page-bg-image", aboveFold: true }) }));
}
// ========== PAGE TITLE HEADER ==========
/**
 * PageTitleHeader — Small H1 header used primarily for page titles.
 *
 * @param {string} [props.title] - Title text to display.
 * @param {string} [props.url] - Optional URL; when present, the title will be rendered as a link.
 * @param {node} [props.children] - Child nodes to render (used as title text if title prop is missing).
 */
PageTitleHeader.propTypes = {
    /** Page title text */
    title: PropTypes.string,
    /** Optional URL to wrap the title with */
    url: PropTypes.string,
    /** Optional child nodes to render below the title */
    children: PropTypes.node
};
export function PageTitleHeader({ title, url, children }) {
    const calloutTarget = url && url.substring(0, 4).toLowerCase() === 'http' ? '_blank' : '_self';
    return (_jsx(_Fragment, { children: url
            ? _jsx("a", { href: url, target: calloutTarget, rel: "noopener noreferrer", children: _jsxs("h1", { className: "page-title-header", children: [title, children && _jsx(_Fragment, { children: children })] }) })
            : _jsxs("h1", { className: "page-title-header", children: [title, children && _jsx(_Fragment, { children: children })] }) }));
}
;
// ========== PAGE SECTION ==========
/**
 * PageSection — Flexible section wrapper used for page layout (grid or flex).
 *
 * @param {string} [props.id] - Optional element id for the section.
 * @param {string} [props.className] - Optional additional CSS class names.
 * @param {oneOf} [props.layoutType] - Layout mode: 'grid' | 'flex' | 'none'.
 * @param {string} [props.gap] - Gap between child elements (CSS gap value).
 * @param {string} [props.maxWidth] - Maximum content width (e.g., '1024px').
 * @param {string} [props.padding] - Section padding (CSS shorthand).
 * @param {string} [props.background] - Background color or CSS value.
 * @param {string} [props.backgroundImage] - Background image URL.
 * @param {number} [props.columns] - Number of grid columns when `layoutType` is 'grid'.
 * @param {oneOf} [props.autoFlow] - Grid auto-flow property (e.g., 'row', 'column', 'dense').
 * @param {oneOf} [props.justifyItems] - Grid justify-items value.
 * @param {shape} [props.responsive] - Responsive column counts ({ mobile, tablet, desktop }).
 * @param {number} [props.mobile] - Mobile column count (inside `responsive`).
 * @param {number} [props.tablet] - Tablet column count (inside `responsive`).
 * @param {number} [props.desktop] - Desktop column count (inside `responsive`).
 * @param {oneOf} [props.direction] - Flex direction when `layoutType` is 'flex'.
 * @param {oneOf} [props.wrap] - Flex wrap property when using flex layout.
 * @param {oneOf} [props.justifyContent] - Flex justify-content value.
 * @param {oneOf} [props.alignItems] - Alignment for cross-axis in grid/flex.
 * @param {node} [props.children] - Child nodes to render inside the section.
 */
PageSection.propTypes = {
    /** Optional element id */
    id: PropTypes.string,
    /** Optional CSS classes */
    className: PropTypes.string,
    /** Layout mode ('grid' | 'flex' | 'none') */
    layoutType: PropTypes.oneOf([...layoutTypes]),
    // Common props
    /** Gap between children (CSS gap) */
    gap: PropTypes.string,
    /** Max width of the content area */
    maxWidth: PropTypes.string,
    /** Padding applied to content area */
    padding: PropTypes.string,
    /** Background color/string */
    background: PropTypes.string,
    /** Background image URL */
    backgroundImage: PropTypes.string,
    // Grid-specific props
    /** Number of columns when using grid layout */
    columns: PropTypes.number,
    /** Grid auto-flow value */
    autoFlow: PropTypes.oneOf([...autoFlowValues]),
    /** Grid justify-items */
    justifyItems: PropTypes.oneOf([...justifyItemsValues]),
    /** Responsive column config */
    responsive: PropTypes.shape({
        /** Mobile column count */
        mobile: PropTypes.number,
        /** Tablet column count */
        tablet: PropTypes.number,
        /** Desktop column count */
        desktop: PropTypes.number,
    }),
    // Flex-specific props
    /** Flex direction value */
    direction: PropTypes.oneOf([...flexDirections]),
    /** Flex wrap behavior */
    wrap: PropTypes.oneOf([...flexWraps]),
    /** Flex justify-content value */
    justifyContent: PropTypes.oneOf([...justifyContentValues]),
    // Shared alignment
    /** Cross-axis alignment for children */
    alignItems: PropTypes.oneOf([...alignItemsValues]),
    /** Child nodes */
    children: PropTypes.node,
};
export function PageSection({ id, className, layoutType = 'grid', gap = '10px', maxWidth = '1024px', padding = '0 20px', /* 5px */ background, backgroundImage, 
// Grid props
columns = 12, autoFlow = 'row', justifyItems = 'stretch', 
// responsive = { mobile: 1, tablet: 2, desktop: 3 },
// Flex props
direction = 'row', wrap = 'wrap', justifyContent = 'start', 
// Shared
alignItems = 'stretch', children, }) {
    const boundaryName = id ? `PageSection:${id}` : 'PageSection';
    const sectionStyle = {
        ...(background && { background }),
    };
    const contentStyle = {
        ...(maxWidth && { maxWidth }),
        margin: '0 auto',
        ...(padding && { padding }),
    };
    // Add layout-specific styles
    if (layoutType === 'grid') {
        return (_jsx(SmartErrorBoundary, { boundaryName: boundaryName, children: _jsxs("section", { id: id || undefined, className: "page-section" + (className ? ` ${className}` : ''), style: sectionStyle, children: [backgroundImage && _jsx(PageSectionBackgroundImage, { backgroundImage: backgroundImage, id: id }), _jsx("div", { className: "page-section-content" + " row-" + columns + "col", style: {
                            ...contentStyle,
                            ...(gap && { gap }),
                            ...(autoFlow && { gridAutoFlow: autoFlow }),
                            ...(alignItems && { alignItems }),
                            ...(justifyItems && { justifyItems }),
                        }, children: children })] }) }));
    }
    if (layoutType === 'flex') {
        return (_jsx(SmartErrorBoundary, { boundaryName: boundaryName, children: _jsxs("section", { id: id || undefined, className: "page-section page-section-flex", style: sectionStyle, children: [backgroundImage && _jsx(PageSectionBackgroundImage, { backgroundImage: backgroundImage, id: id }), _jsx("div", { className: "page-section-content", style: {
                            ...contentStyle,
                            display: 'flex',
                            ...(direction && { flexDirection: direction }),
                            ...(wrap && { flexWrap: wrap }),
                            ...(gap && { gap }),
                            ...(alignItems && { alignItems: alignItems }),
                            ...(justifyContent && { justifyContent: justifyContent }),
                        }, children: children })] }) }));
    }
    // layoutType === 'none'
    return (_jsx(SmartErrorBoundary, { boundaryName: boundaryName, children: _jsxs("section", { id: id || undefined, className: "page-section page-section-none", style: sectionStyle, children: [backgroundImage && _jsx(PageSectionBackgroundImage, { backgroundImage: backgroundImage, id: id }), _jsx("div", { className: "page-section-content", style: contentStyle, children: children })] }) }));
}
// ========== PAGE SECTION HEADER ==========
/**
 * PageSectionHeader — Small header used to label a `PageSection`.
 *
 * @param {string} [props.title] - Section title text.
 * @param {string} [props.url] - Optional URL to link the section title.
 * @param {node} [props.children] - Child nodes to render (used as title text if title prop is missing).
 */
PageSectionHeader.propTypes = {
    /** Section title text */
    title: PropTypes.string,
    /** Optional URL to link the title */
    url: PropTypes.string,
    /** Optional child nodes */
    children: PropTypes.node
};
export function PageSectionHeader({ title, url, children }) {
    const calloutTarget = url && url.substring(0, 4).toLowerCase() === 'http' ? '_blank' : '_self';
    return (_jsx(_Fragment, { children: url
            ? _jsx("a", { href: url, target: calloutTarget, rel: "noopener noreferrer", children: _jsxs("h2", { className: "page-section-header", suppressHydrationWarning: true, children: [title, children && _jsx(_Fragment, { children: children })] }) })
            : _jsxs("h2", { className: "page-section-header", suppressHydrationWarning: true, children: [title, children && _jsx(_Fragment, { children: children })] }) }));
}
;
// ========== PAGE SECTION BACKGROUND IMAGE ==========
/**
 * PageSectionBackgroundImage — Decorative background image used by `PageSection`.
 *
 * @param {string} [props.backgroundImage] - Background image URL (required).
 * @param {string} [props.id] - Optional id used for element ids and titles.
 */
PageSectionBackgroundImage.propTypes = {
    /** Background image URL */
    backgroundImage: PropTypes.string.isRequired,
    /** Optional id for the image element */
    id: PropTypes.string,
};
export function PageSectionBackgroundImage(props) {
    const config = usePixelatedConfig();
    return (_jsx(_Fragment, { children: _jsx(SmartImage, { src: props.backgroundImage, className: "section-background-image", id: props.id ? `${props.id}-background-image` : undefined, 
            // name={props.id ? `${props.id} background image` : undefined}
            title: props.id ? `${props.id} background image` : undefined, alt: props.id ? `${props.id} background image` : "", cloudinaryEnv: config?.integrations?.cloudinary?.product_env ?? undefined, cloudinaryDomain: config?.integrations?.cloudinary?.baseUrl ?? undefined, cloudinaryTransforms: config?.integrations?.cloudinary?.transforms ?? undefined }) }));
}
// ========== GRID ITEM ==========
/**
 * PageGridItem — Single grid cell wrapper that supports span and positioning helpers.
 *
 * @param {string} [props.id] - Optional id for the grid item element.
 * @param {string} [props.className] - Additional CSS classes for the item.
 * @param {number} [props.columnSpan] - How many columns the item spans.
 * @param {number} [props.rowSpan] - How many rows the item spans.
 * @param {number} [props.columnStart] - Starting column index for the item.
 * @param {number} [props.columnEnd] - Ending column index for the item.
 * @param {number} [props.rowStart] - Starting row index for the item.
 * @param {number} [props.rowEnd] - Ending row index for the item.
 * @param {oneOf} [props.alignSelf] - Cross-axis alignment for the item.
 * @param {oneOf} [props.justifySelf] - Main-axis alignment for the item.
 * @param {node} [props.children] - Child nodes to render inside the grid cell.
 */
PageGridItem.propTypes = {
    /** Optional element id */
    id: PropTypes.string,
    /** Optional CSS classes */
    className: PropTypes.string,
    /** Column span value */
    columnSpan: PropTypes.number,
    /** Row span value */
    rowSpan: PropTypes.number,
    /** Column start index */
    columnStart: PropTypes.number,
    /** Column end index */
    columnEnd: PropTypes.number,
    /** Row start index */
    rowStart: PropTypes.number,
    /** Row end index */
    rowEnd: PropTypes.number,
    /** Cross-axis alignment */
    alignSelf: PropTypes.oneOf(['start', 'center', 'end', 'stretch']),
    /** Main-axis alignment */
    justifySelf: PropTypes.oneOf(['start', 'center', 'end', 'stretch']),
    /** Child nodes */
    children: PropTypes.node,
};
export function PageGridItem({ id, className, columnSpan, rowSpan, columnStart, columnEnd, rowStart, rowEnd, alignSelf, justifySelf, children, }) {
    const itemStyle = {
        ...(columnSpan && !columnStart && { gridColumn: ` span ${columnSpan}` }),
        // columnStart && columnSpan = style grid-s##-w##
        // columnStart && columnEnd = style grid-s##-e##
        // ...(columnStart && columnEnd && { gridColumn: ` ${columnStart} / ${columnEnd}` }),
        ...(rowSpan && { gridRow: ` span ${rowSpan}` }),
        ...(rowStart && rowEnd && { gridRow: ` ${rowStart} / ${rowEnd}` }),
        ...(alignSelf && { alignSelf }),
        ...(justifySelf && { justifySelf }),
    };
    return (_jsx("div", { className: "grid-item" +
            (className ? ` ${className}` : '') +
            (columnStart && columnSpan && !columnEnd ? ` grid-s${columnStart}-w${columnSpan}` : '') +
            (columnStart && columnEnd && !columnSpan ? ` grid-s${columnStart}-e${columnEnd}` : ''), id: (id) ? id : undefined, style: itemStyle, children: children }));
}
// ========== FLEX ITEM ==========
/**
 * PageFlexItem — Simple flex item helper that exposes CSS flex and order helpers.
 *
 * @param {string} [props.flex] - CSS flex shorthand value (e.g., '1 1 auto').
 * @param {number} [props.order] - Order value used by flexbox ordering.
 * @param {oneOf} [props.alignSelf] - Override for cross-axis alignment on the item.
 * @param {node} [props.children] - Child nodes to render inside the flex item.
 */
PageFlexItem.propTypes = {
    /** CSS flex shorthand value */
    flex: PropTypes.string,
    /** Flex order value */
    order: PropTypes.number,
    /** Cross-axis alignment override */
    alignSelf: PropTypes.oneOf(['auto', 'start', 'center', 'end', 'stretch', 'baseline']),
    /** Child nodes */
    children: PropTypes.node,
};
export function PageFlexItem({ flex = '1', order, alignSelf, children, }) {
    const itemStyle = {
        ...(flex && { flex }),
        ...(order !== undefined && order !== null && { order }),
        ...(alignSelf && { alignSelf: alignSelf }),
    };
    return (_jsx("div", { className: "flex-item", style: itemStyle, children: children }));
}
// ========== PAGE LINK ==========
const pageLinkShape = PropTypes.shape({
    label: PropTypes.string.isRequired,
    href: PropTypes.string.isRequired,
    target: PropTypes.oneOf(["_self", "_blank"]),
});
// ========== PAGE HEADER ==========
/**
 * PageHeader — Top page header block that can include eyebrow, headline, description and CTA.
 *
 * @param {string} [props.className] - Optional CSS classes to apply to the header.
 * @param {string} [props.eyebrow] - Small eyebrow text shown above the headline.
 * @param {string} [props.headline] - Main headline text.
 * @param {string} [props.description] - Short description or subheading text.
 * @param {string} [props.ctaLabel] - CTA button label text.
 * @param {string} [props.ctaHref] - CTA URL to navigate to when clicked.
 * @param {oneOf} [props.ctaTarget] - Optional target for CTA link ('_self' or '_blank').
 * @param {node} [props.children] - Additional nodes to render inside the header.
 * @param {boolean} [props.fixed] - When true, header becomes fixed and a spacer is inserted.
 */
PageHeader.propTypes = {
    /** Optional CSS classes */
    className: PropTypes.string,
    /** Eyebrow above the headline */
    eyebrow: PropTypes.string,
    /** Headline text */
    headline: PropTypes.string,
    /** Subheading or description */
    description: PropTypes.string,
    /** CTA label */
    ctaLabel: PropTypes.string,
    /** CTA href */
    ctaHref: PropTypes.string,
    /** CTA target */
    ctaTarget: PropTypes.oneOf(["_self", "_blank"]),
    /** Additional child nodes */
    children: PropTypes.node,
    /** When true, header is fixed (sticky) */
    fixed: PropTypes.bool,
};
export function PageHeader({ className, eyebrow, headline, description, ctaLabel, ctaHref, ctaTarget, children, fixed = false, }) {
    const resolvedTarget = ctaTarget ?? (ctaHref && ctaHref.startsWith("http") ? "_blank" : "_self");
    const rel = resolvedTarget === "_blank" ? "noopener noreferrer" : undefined;
    const headerRef = useRef(null);
    const [spacerHeight, setSpacerHeight] = useState(undefined);
    useLayoutEffect(() => {
        if (!fixed) {
            setSpacerHeight(undefined);
            return;
        }
        const updateHeight = () => {
            if (!headerRef.current) {
                return;
            }
            setSpacerHeight(headerRef.current.getBoundingClientRect().height);
        };
        updateHeight();
        window.addEventListener("resize", updateHeight);
        return () => window.removeEventListener("resize", updateHeight);
    }, [fixed, eyebrow, headline, description, ctaLabel, ctaHref]);
    const spacerStyle = spacerHeight !== undefined ? { height: `${spacerHeight}px` } : undefined;
    const headerClasses = `page-header${className ? ` ${className}` : ""}${fixed ? " fixed-header" : ""}`;
    return (_jsxs(_Fragment, { children: [fixed && (_jsx("div", { className: "page-header-spacer", "aria-hidden": "true", style: spacerStyle })), _jsxs("header", { ref: headerRef, className: headerClasses, children: [eyebrow && _jsx("p", { className: "page-header-eyebrow", children: eyebrow }), headline && _jsx("h1", { children: headline }), description && _jsx("p", { className: "page-header-description", children: description }), ctaLabel && ctaHref && (_jsx("a", { className: "page-header-cta", href: ctaHref, target: resolvedTarget, rel: rel, children: ctaLabel })), children] })] }));
}
// ========== PAGE MAIN ==========
/**
 * PageMain — Main content wrapper with optional width and padding controls.
 *
 * @param {string} [props.id] - Optional id attribute for the main element.
 * @param {string} [props.className] - Additional CSS classes to apply.
 * @param {string} [props.maxWidth] - Maximum content width (e.g., '1200px').
 * @param {string} [props.padding] - Padding around the main content (CSS shorthand).
 * @param {node} [props.children] - Main content nodes.
 */
PageMain.propTypes = {
    /** Optional id */
    id: PropTypes.string,
    /** Optional CSS classes */
    className: PropTypes.string,
    /** Max content width */
    maxWidth: PropTypes.string,
    /** Padding for main content */
    padding: PropTypes.string,
    /** Child nodes */
    children: PropTypes.node,
};
export function PageMain({ id, className, maxWidth = "1200px", padding = "0 20px 60px", children, }) {
    const layoutStyle = {
        ...(maxWidth && { maxWidth }),
        ...(padding && { padding }),
    };
    return (_jsx("main", { id: id || undefined, className: `page-main${className ? ` ${className}` : ""}`, style: layoutStyle, children: children }));
}
// ========== PAGE NAV ==========
/**
 * PageNav — Simple navigation helper that renders link items horizontally or vertically.
 *
 * @param {string} [props.className] - Optional CSS classes for the nav wrapper.
 * @param {oneOf} [props.orientation] - Layout orientation: 'horizontal' or 'vertical'.
 * @param {arrayOf} [props.links] - Array of link objects ({label, href, target}).
 */
PageNav.propTypes = {
    /** Optional CSS classes */
    className: PropTypes.string,
    /** Orientation: 'horizontal' | 'vertical' */
    orientation: PropTypes.oneOf(["horizontal", "vertical"]),
    /** Array of link objects */
    links: PropTypes.arrayOf(pageLinkShape),
};
export function PageNav({ className, orientation = "horizontal", links, }) {
    const resolvedLinks = Array.isArray(links)
        ? links.filter(Boolean)
        : [];
    if (!resolvedLinks.length) {
        return null;
    }
    return (_jsx("nav", { className: `page-nav page-nav-${orientation}` + (className ? ` ${className}` : ""), children: resolvedLinks.map((link) => {
            const target = link.target ?? (link.href.startsWith("http") ? "_blank" : "_self");
            const rel = target === "_blank" ? "noopener noreferrer" : undefined;
            return (_jsx("a", { href: link.href, target: target, rel: rel, children: link.label }, `${link.href}-${link.label}`));
        }) }));
}
// ========== PAGE HTML ==========
/**
 * PageHTML — Simple component to render raw HTML content.
 *
 * @param {string} [props.html] - Raw HTML string to render.
 * @param {node} [props.children] - Optional child nodes to render if `html` is not provided.
 *
 * @returns A div containing the rendered HTML or children.
 */
PageHTML.propTypes = {
    /** Raw HTML content */
    html: PropTypes.string,
    /** Optional CSS class */
    className: PropTypes.string,
    /** Children to render as fallback or addition (node) */
    children: PropTypes.node
};
export function PageHTML({ html, className, children }) {
    const combinedClassName = `page-html-content${className ? ` ${className}` : ""}`;
    if (html) {
        return _jsx("div", { className: combinedClassName, dangerouslySetInnerHTML: { __html: html } });
    }
    return _jsx("div", { className: combinedClassName, children: children });
}
// ========== PAGE FOOTER ==========
/**
 * PageFooter — Page footer that optionally renders text, links and children.
 *
 * @param {string} [props.className] - Optional additional CSS classes.
 * @param {string} [props.text] - Footer text or short copyright string.
 * @param {arrayOf} [props.links] - Array of link objects to show in footer.
 * @param {node} [props.children] - Additional nodes rendered inside the footer.
 */
PageFooter.propTypes = {
    /** Optional CSS classes */
    className: PropTypes.string,
    /** Footer text */
    text: PropTypes.string,
    /** Footer links */
    links: PropTypes.arrayOf(pageLinkShape),
    /** Child nodes */
    children: PropTypes.node,
};
export function PageFooter({ className, text, links, children, }) {
    const resolvedLinks = Array.isArray(links)
        ? links.filter(Boolean)
        : [];
    const hasLinks = resolvedLinks.length > 0;
    if (!text && !hasLinks && !children) {
        return null;
    }
    return (_jsxs("footer", { className: `page-footer${className ? ` ${className}` : ""}`, children: [text && _jsx("p", { className: "page-footer-text", children: text }), hasLinks && (_jsx("div", { className: "page-footer-links", children: resolvedLinks.map((link) => {
                    const target = link.target ?? (link.href.startsWith("http") ? "_blank" : "_self");
                    const rel = target === "_blank" ? "noopener noreferrer" : undefined;
                    return (_jsx("a", { href: link.href, target: target, rel: rel, children: link.label }, `${link.href}-${link.label}`));
                }) })), children] }));
}
